import secureApi from '../../utils/secureApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export interface Transaction {
  _id: string;
  transactionId: string;
  tournamentName: string;
  team: string;
  amount: number;
  paymentMethod: string;
  status: 'Success' | 'Failed' | 'Pending';
  date: string;
  description: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  userDetails?: {
    name: string;
    email: string;
    phone: string;
  };
}

// Get list of all transactions
export const getTransactionsList = async (filters?: {
  status?: string;
  paymentMethod?: string;
  dateRange?: string;
  startDate?: string;
  endDate?: string;
}) => {
  try {
    const response = await secureApi.get('/transactions', {
      params: filters
    });
    return response;
  } catch (error) {
    console.error('Error fetching transactions list:', error);
    throw error;
  }
};

// Get specific transaction detail
export const getTransactionDetail = async (transactionId: string) => {
  try {
    const response = await secureApi.get(`/transactions/${transactionId}`);
    return response;
  } catch (error) {
    console.error('Error fetching transaction detail:', error);
    throw error;
  }
};

// Get transaction statistics
export const getTransactionStats = async (dateRange?: string) => {
  try {
    const response = await secureApi.get('/transactions/stats', {
      params: { dateRange }
    });
    return response;
  } catch (error) {
    console.error('Error fetching transaction stats:', error);
    throw error;
  }
};

// Export transactions to CSV/Excel
export const exportTransactions = async (filters?: {
  status?: string;
  paymentMethod?: string;
  dateRange?: string;
  format?: 'csv' | 'excel';
}) => {
  try {
    const response = await secureApi.get('/transactions/export', {
      params: filters,
      responseType: 'blob'
    });
    return response;
  } catch (error) {
    console.error('Error exporting transactions:', error);
    throw error;
  }
};

// Retry failed transaction
export const retryTransaction = async (transactionId: string) => {
  try {
    const response = await secureApi.post(`/transactions/${transactionId}/retry`, {});
    return response;
  } catch (error) {
    console.error('Error retrying transaction:', error);
    throw error;
  }
};

// Cancel pending transaction
export const cancelTransaction = async (transactionId: string, reason?: string) => {
  try {
    const response = await secureApi.post(`/transactions/${transactionId}/cancel`, {
      reason
    });
    return response;
  } catch (error) {
    console.error('Error canceling transaction:', error);
    throw error;
  }
};

// Get tournament registration transactions with pagination
export const getTournamentRegistrationTransactions = async (pageNum: number = 1, filters?: {
  status?: string;
  paymentMethod?: string;
  dateRange?: string;
  startDate?: string;
  endDate?: string;
}) => {
  try {
    // Prepare query parameters for the paginated API endpoint
    const params: any = {
      pageNum
    };
    
    // Add filters to query parameters for server-side filtering
    if (filters) {
      if (filters.status && filters.status !== 'All') {
        params.paidStatus = filters.status;
      }
      if (filters.paymentMethod && filters.paymentMethod !== 'All') {
        params.method = filters.paymentMethod;
      }
      if (filters.dateRange && filters.dateRange !== 'All') {
        params.dateRange = filters.dateRange;
      }
      if (filters.startDate) {
        params.startDate = filters.startDate;
      }
      if (filters.endDate) {
        params.endDate = filters.endDate;
      }
    }
    
    // Use the proper paginated API endpoint
    const response = await secureApi.get('/api/v1/payments/tournament-registration-transactions', {
      params
    });
    
    console.log('API parameters:', params);
    
    // Handle the API response structure
    if (response && response.data) {
      // The API should return a paginated structure
      if (response.data.paginatedResults || response.data.transactions) {
        const transactions = response.data.paginatedResults || response.data.transactions || [];
        const totalCount = response.data.totalCount || 0;
        const totalPages = response.data.totalPages || Math.ceil(totalCount / 10);
        
        return {
          data: {
            paginatedResults: transactions,
            totalCount,
            totalPages,
            currentPage: pageNum
          }
        };
      } else if (Array.isArray(response.data)) {
        // Fallback for array response
        return {
          data: {
            paginatedResults: response.data,
            totalCount: response.data.length,
            totalPages: Math.ceil(response.data.length / 10),
            currentPage: pageNum
          }
        };
      } else {
        console.error('Unexpected response structure for transactions:', response.data);
        throw new Error((response.data as any).message || 'Failed to fetch transactions');
      }
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching tournament registration transactions:', error);
    throw error;
  }
}; 